/**
 * Framework-agnostic conversation-session controller behind
 * `useMagnusConversation` (plan 019). Composes the chat-history store and
 * absorbs the session plumbing every consumer had been hand-rolling:
 * mount-time restore of a persisted chat id, fresh-chat creation gated on
 * that restore settling (ungated, the two race and reloads mint stray empty
 * chats), and pinning the active chat id back into the persistence seam.
 *
 * Fresh sessions NEVER call `chats.create`: the store mints a client-side
 * id (same lazy pattern as the flagship `/chat` route's `nanoid()`) and the
 * server materializes the row on the first POST `/api/chat` (`ensureChat`)
 * — or earlier via the upload route's `ensureExists` stub if the user
 * attaches before sending. Clicking "new chat" therefore leaves no orphan
 * empty chat in the DB/sidebar, and an unauthenticated surface fires zero
 * creates (the pre-024 eager create could also loop on 401 until the
 * failure guard landed; the lazy path removes the loop's reason to exist).
 */

import type { MagnusUIMessage } from "@magnus/contract/messages";
import type { MagnusClient } from "../client";
import {
  bindHistoryActions,
  type ChatHistoryStore,
  createChatHistoryStore,
  type MagnusChatHistorySource,
} from "./source-stores";

/**
 * Where the active chat id survives a reload. `FloatingUrlPersistence`
 * (from the floating block / `@bg-bgi/magnus-ui/floating`)
 * satisfies this structurally; any storage works.
 */
export interface MagnusChatIdPersistence {
  readChatId(): string | null;
  writeChatId(chatId: string | null): void;
}

export interface ConversationStoreState {
  /** In-flight run id on a restored/selected chat — wire to `useMagnus`'s
   *  `activeStreamId` so a reopened chat resumes its live stream. */
  activeStreamId?: string | null;
  /** The chat to drive: the restored/selected chat, or the session's
   *  client-minted id (materialized server-side on first send). `undefined`
   *  only while a persisted id is still restoring. */
  chatId?: string;
  error?: Error;
  /** Hydrated transcript for a restored/selected chat. */
  initialMessages?: MagnusUIMessage[];
  /** True while a restore/hydration is in flight. Fresh sessions resolve
   *  immediately — the minted id needs no server round-trip. */
  loading: boolean;
  /** Remount key for the chat component — changes on selection / New chat. */
  sessionKey: string;
}

export interface ConversationStore {
  /** Drop the store, releasing the history store's pending settle re-poll.
   *  The hook calls this when the store is replaced or unmounted. */
  dispose(): void;
  getState(): ConversationStoreState;
  /** Prop-ready history source, with persistence pinning built in. */
  history: MagnusChatHistorySource;
  /** Wire to the chat's turn lifecycle so the recents list stays fresh. */
  onFinish(): void;
  onFirstStream(): void;
  /** Wire to `useMagnus`'s `onStop`: stops the run server-side and
   *  persists the partial transcript so a reload shows it. */
  onStop(partialMessages: MagnusUIMessage[]): Promise<void>;
  /** Switch to a chat by id. A minted chat that was never sent reopens as
   *  the fresh session it was, rather than a selection the server would
   *  404 and a pin a reload couldn't restore. */
  open(chatId: string): void;
  /** Refetch the recents list; resolves once the fetch settles. */
  refresh(): Promise<void>;
  subscribe(listener: () => void): () => void;
}

export function createConversationStore(options: {
  client: MagnusClient;
  /** Page size for the recents list. Defaults to 20. */
  limit?: number;
  persistence?: MagnusChatIdPersistence;
}): ConversationStore {
  const { client, limit, persistence } = options;
  const inner: ChatHistoryStore = createChatHistoryStore({ client, limit });

  const listeners = new Set<() => void>();
  const emit = () => {
    for (const listener of [...listeners]) {
      listener();
    }
  };

  // Read the pinned id once, up front: the restore must win over minting.
  const restoreId = persistence?.readChatId() ?? null;
  let restorePending = restoreId !== null;

  // The fresh-session id, minted lazily per sessionKey. Never sent to
  // `chats.create` — the first send's POST /api/chat creates the row with
  // this id server-side (the flagship `/chat` page mints the same way).
  let minted: { chatId: string; sessionKey: string } | null = null;
  // Pin a minted id only once the chat is REAL (a turn streamed/settled) —
  // pinning on mint would stuff `?mchat=` with ids of chats never sent.
  let mintedHasContent = false;
  // Minted ids this session left before sending anything.
  const unsent = new Set<string>();
  let pinnedChatId: string | null = null;

  const currentChatId = (): string | undefined => {
    const h = inner.getState();
    if (h.active) {
      return h.active.chatId;
    }
    if (restorePending) {
      return undefined;
    }
    if (minted?.sessionKey !== h.sessionKey) {
      if (minted && !mintedHasContent) {
        unsent.add(minted.chatId);
      }
      minted = { chatId: crypto.randomUUID(), sessionKey: h.sessionKey };
      mintedHasContent = false;
    }
    return minted.chatId;
  };

  const computeState = (): ConversationStoreState => {
    const h = inner.getState();
    const chatId = currentChatId();
    return {
      chatId,
      activeStreamId: h.active?.activeStreamId ?? null,
      error: h.error,
      initialMessages: h.active?.initialMessages,
      loading: restorePending || h.loadingActive,
      sessionKey: h.sessionKey,
    };
  };

  let state = computeState();

  const pin = () => {
    const h = inner.getState();
    const chatId = currentChatId();
    if (restorePending || !chatId || chatId === pinnedChatId) {
      return;
    }
    // Restored/selected chats are already real server-side; a minted id is
    // pinned only after its first turn (below) so reloads never try to
    // restore a chat that was never sent.
    if (!(h.active || mintedHasContent)) {
      return;
    }
    pinnedChatId = chatId;
    persistence?.writeChatId(chatId);
  };

  const noteFirstTurn = () => {
    if (minted?.sessionKey === inner.getState().sessionKey) {
      mintedHasContent = true;
    }
    pin();
  };

  const update = () => {
    pin();
    state = computeState();
    emit();
  };

  let started = false;
  const start = () => {
    if (started) {
      return;
    }
    started = true;
    if (restoreId !== null) {
      // A failed restore settles too — a fresh minted id takes over.
      inner.select(restoreId).finally(() => {
        restorePending = false;
        update();
      });
    }
  };

  /** Forget the session's reference to a chat: the pin, the persisted id, and
   *  the minted id, so the next tick mints a fresh chat instead. */
  const forgetSession = () => {
    pinnedChatId = null;
    persistence?.writeChatId(null);
    minted = null;
    mintedHasContent = false;
  };

  /**
   * Deleting the chat the session is driving has to unwind the session too —
   * the inner store only resets when the deleted chat was the *hydrated* one,
   * so a freshly created chat (never selected, so `active` is null) would
   * otherwise leave the composer pointed at a deleted row.
   */
  const removeChat = async (chatId: string) => {
    const wasCurrent = currentChatId() === chatId;
    const wasActive = inner.getState().active?.chatId === chatId;
    await inner.remove(chatId);
    if (!wasCurrent) {
      return;
    }
    forgetSession();
    if (!wasActive) {
      inner.newChat();
    }
    update();
  };

  const history: MagnusChatHistorySource = {
    get items() {
      return inner.getState().items;
    },
    get loading() {
      return inner.getState().loadingList;
    },
    // Manual/refresh-on-open invalidation for the switcher dropdowns. The
    // list already self-refreshes on mount-subscribe, new-chat, and turn
    // settle; this covers the recovery case (an initial load that failed
    // or went stale while the panel sat open). Returns the load's settle
    // promise so switchers can drive a local spinner off the explicit
    // refresh rather than the store-wide loading flag.
    refresh: () => inner.refresh(),
    // Lets long-lived open switchers re-render on list changes even when an
    // ancestor's own subscription was silenced by the React 19.2 StrictMode
    // + Suspense reconciler bug (see source-stores.ts `MagnusChatHistorySource`).
    subscribe: (listener) => inner.subscribe(listener),
    onNewChat: () => {
      forgetSession();
      inner.newChat();
    },
    onSelect: (chatId) => {
      pinnedChatId = chatId;
      persistence?.writeChatId(chatId);
      return inner.select(chatId);
    },
    // Bound so a failed mutation logs instead of rejecting into the switcher's
    // fire-and-forget call (unhandled rejection otherwise).
    ...bindHistoryActions({ ...inner, remove: removeChat }),
  };

  const open = (chatId: string) => {
    if (currentChatId() === chatId) {
      return;
    }
    const isUnsent =
      unsent.has(chatId) || (minted?.chatId === chatId && !mintedHasContent);
    if (!isUnsent) {
      history.onSelect(chatId);
      return;
    }
    unsent.delete(chatId);
    if (minted && minted.chatId !== chatId && !mintedHasContent) {
      unsent.add(minted.chatId);
    }
    inner.newChat();
    minted = { chatId, sessionKey: inner.getState().sessionKey };
    mintedHasContent = false;
    update();
  };

  return {
    dispose: inner.dispose,
    getState: () => state,
    history,
    open,
    onFinish: () => {
      noteFirstTurn();
      inner.notifyTurnSettled();
    },
    onFirstStream: () => {
      noteFirstTurn();
      inner.refresh();
    },
    onStop: async (partialMessages) => {
      const chatId = currentChatId();
      if (!chatId) {
        return;
      }
      await client.chats.stop(chatId, partialMessages);
    },
    refresh: inner.refresh,
    subscribe: (listener) => {
      listeners.add(listener);
      const unsubscribeInner = inner.subscribe(update);
      start();
      return () => {
        listeners.delete(listener);
        unsubscribeInner();
      };
    },
  };
}
